import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Form,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  InputGroup,
} from "react-bootstrap";
import {
  FaEdit,
  FaPlus,
  FaEye,
  FaCheck,
  FaTimes,
  FaEnvelope,
  FaBell,
  FaInfoCircle,
} from "react-icons/fa";

import {
  getCommunicationRules,
  getTemplatesByRule,
  createEmailTemplate,
  updateEmailTemplate,
  createInPlatformTemplate,
  updateInPlatformTemplate,
  createBothTemplates,
  previewTemplates,
  getAssessmentScreener,
} from "../services/APIClient";

function ManageTemplatesModal({ show, onHide, onTemplateUpdated }) {
  const [rules, setRules] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [templates, setTemplates] = useState({ email: null, inPlatform: null });
  const [loading, setLoading] = useState(false);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form states
  const [showEditor, setShowEditor] = useState(false);
  const [activeTab, setActiveTab] = useState("email");
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Form data
  const [emailFormData, setEmailFormData] = useState({
    subject: "",
    body: "",
  });

  const [inPlatformFormData, setInPlatformFormData] = useState({
    title: "",
    body: "",
    messageType: "notification",
    priority: "medium",
    actionUrl: "",
    expiresAfterDays: 30,
  });

  // Available variables for templates
  const availableVariables = [
    { name: "schoolName", description: "School name" },
    { name: "percentage", description: "Completion percentage" },
    { name: "studentsScreened", description: "Number of students screened" },
    { name: "totalStudents", description: "Total number of students" },
    { name: "assessmentName", description: "Assessment name" },
    { name: "gradeLevel", description: "Grade level" },
    { name: "teacherName", description: "Recipient's name" },
  ];

  const messageTypes = ["notification", "alert", "reminder", "info"];
  const priorities = ["low", "medium", "high", "urgent"];

  useEffect(() => {
    if (show) {
      loadRules();
      loadAssessments();
    }
  }, [show]);

  const loadAssessments = async () => {
    setLoadingAssessments(true);
    try {
      const data = await getAssessmentScreener({ active: true });
      setAssessments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load assessments:", err);
      setAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const getAssessmentDisplay = (assessmentId) => {
    const assessment = assessments.find(a => a.id === assessmentId);
    if (assessment) {
      return assessment.name;
    }
    return assessmentId; // Fallback to ID if not found
  };

  const loadRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const rulesData = await getCommunicationRules();
      
      // Fetch template status for each rule
      const rulesWithTemplateStatus = await Promise.all(
        (Array.isArray(rulesData) ? rulesData : []).map(async (rule) => {
          try {
            const templates = await getTemplatesByRule(rule.id);
            return {
              ...rule,
              hasEmailTemplate: !!templates?.email,
              hasInPlatformTemplate: !!templates?.inPlatform,
            };
          } catch {
            return {
              ...rule,
              hasEmailTemplate: false,
              hasInPlatformTemplate: false,
            };
          }
        })
      );
      
      setRules(rulesWithTemplateStatus);
    } catch (err) {
      setError("Failed to load rules: " + err.message);
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplatesForRule = async (rule) => {
    setLoading(true);
    setError(null);
    setSelectedRule(rule);
    try {
      const data = await getTemplatesByRule(rule.id);
      setTemplates(data);

      // Populate form data if templates exist
      if (data.email) {
        setEmailFormData({
          subject: data.email.subject,
          body: data.email.body,
        });
      }

      if (data.inPlatform) {
        setInPlatformFormData({
          title: data.inPlatform.title,
          body: data.inPlatform.body,
          messageType: data.inPlatform.messageType,
          priority: data.inPlatform.priority,
          actionUrl: data.inPlatform.actionUrl || "",
          expiresAfterDays: data.inPlatform.expiresAfterDays || 30,
        });
      }

      setShowEditor(true);
    } catch (err) {
      setError("Failed to load templates: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplates = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!emailFormData.subject || !emailFormData.body) {
      setError("Email subject and body are required");
      return;
    }

    if (!inPlatformFormData.title || !inPlatformFormData.body) {
      setError("In-platform title and body are required");
      return;
    }

    setLoading(true);

    try {
      if (templates.email || templates.inPlatform) {
        // Update existing templates
        if (templates.email) {
          await updateEmailTemplate(templates.email.id, emailFormData);
        } else {
          await createEmailTemplate({
            ruleId: selectedRule.id,
            ruleType: "assessment",
            ...emailFormData,
          });
        }

        if (templates.inPlatform) {
          await updateInPlatformTemplate(
            templates.inPlatform.id,
            inPlatformFormData
          );
        } else {
          await createInPlatformTemplate({
            ruleId: selectedRule.id,
            ruleType: "assessment",
            ...inPlatformFormData,
          });
        }
      } else {
        // Create both templates at once
        await createBothTemplates(selectedRule.id, {
          ruleType: "assessment",
          email: emailFormData,
          inPlatform: inPlatformFormData,
        });
      }

      setSuccess("Templates saved successfully!");
      if (onTemplateUpdated) onTemplateUpdated();

      // Reload templates and rules
      await loadTemplatesForRule(selectedRule);
      await loadRules(); // Refresh the rules list to update template status badges
    } catch (err) {
      setError("Failed to save templates: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setError(null);

    try {
      const previewData = await previewTemplates({
        ruleId: selectedRule.id,
        ruleType: "assessment",
        sampleData: {
          schoolName: "Example Elementary School",
          percentage: selectedRule.threshold,
          studentsScreened: 75,
          totalStudents: 100,
          schoolId: "sample-123",
          assessmentName: "Literacy Screening",
          gradeLevel: 2,
          teacherName: "Ms. Smith",
        },
      });

      setPreview(previewData);
      setShowPreview(true);
    } catch (err) {
      console.error("Preview API failed, using client-side preview:", err);
      
      // Fallback to client-side preview
      const sampleData = {
        schoolName: "Example Elementary School",
        percentage: selectedRule.threshold,
        studentsScreened: 75,
        totalStudents: 100,
        schoolId: "sample-123",
        assessmentName: "Literacy Screening",
        gradeLevel: 2,
        teacherName: "Ms. Smith",
      };
      
      // Simple variable replacement
      const replaceVariables = (text) => {
        return text
          .replace(/\{\{schoolName\}\}/g, sampleData.schoolName)
          .replace(/\{\{percentage\}\}/g, sampleData.percentage)
          .replace(/\{\{studentsScreened\}\}/g, sampleData.studentsScreened)
          .replace(/\{\{totalStudents\}\}/g, sampleData.totalStudents)
          .replace(/\{\{assessmentName\}\}/g, sampleData.assessmentName)
          .replace(/\{\{gradeLevel\}\}/g, sampleData.gradeLevel)
          .replace(/\{\{teacherName\}\}/g, sampleData.teacherName);
      };
      
      // Calculate expiration date
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + (inPlatformFormData.expiresAfterDays || 30));
      
      setPreview({
        email: {
          subject: replaceVariables(emailFormData.subject),
          body: replaceVariables(emailFormData.body),
        },
        inPlatform: {
          title: replaceVariables(inPlatformFormData.title),
          body: replaceVariables(inPlatformFormData.body),
          messageType: inPlatformFormData.messageType,
          priority: inPlatformFormData.priority,
          actionUrl: inPlatformFormData.actionUrl,
          expiresAt: expiresAt.toISOString(),
        },
      });
      
      setShowPreview(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBack = () => {
    setShowEditor(false);
    setSelectedRule(null);
    setTemplates({ email: null, inPlatform: null });
    setEmailFormData({ subject: "", body: "" });
    setInPlatformFormData({
      title: "",
      body: "",
      messageType: "notification",
      priority: "medium",
      actionUrl: "",
      expiresAfterDays: 30,
    });
  };

  const insertVariable = (variable, field) => {
    const placeholder = `{{${variable}}}`;

    if (activeTab === "email") {
      if (field === "subject") {
        setEmailFormData({
          ...emailFormData,
          subject: emailFormData.subject + placeholder,
        });
      } else {
        setEmailFormData({
          ...emailFormData,
          body: emailFormData.body + placeholder,
        });
      }
    } else {
      if (field === "title") {
        setInPlatformFormData({
          ...inPlatformFormData,
          title: inPlatformFormData.title + placeholder,
        });
      } else {
        setInPlatformFormData({
          ...inPlatformFormData,
          body: inPlatformFormData.body + placeholder,
        });
      }
    }
  };

  const VariableHelper = () => (
    <Card className="mb-3" style={{ backgroundColor: "#f8f9fa" }}>
      <Card.Body>
        <h6 className="mb-2">
          <FaInfoCircle style={{ marginRight: "0.5rem" }} />
          Available Variables
        </h6>
        <p className="small text-muted mb-2">
          Click a variable to insert it at the cursor position:
        </p>
        <div className="d-flex flex-wrap gap-1">
          {availableVariables.map((v) => (
            <Button
              key={v.name}
              size="sm"
              variant="outline-primary"
              onClick={() => insertVariable(v.name, activeTab === "email" ? "body" : "body")}
              title={v.description}
            >
              {`{{${v.name}}}`}
            </Button>
          ))}
        </div>
        <small className="text-muted d-block mt-2">
          Variables will be replaced with actual values when messages are sent.
        </small>
      </Card.Body>
    </Card>
  );

  return (
    <>
      <Modal show={show && !showPreview} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {showEditor
              ? `Edit Templates - ${selectedRule?.eventType}`
              : "Manage Message Templates"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Success/Error Messages */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* List View */}
          {!showEditor ? (
            <>
              <div className="mb-3">
                <h5>Select a Rule to Edit Templates</h5>
                <p className="text-muted">
                  Each communication rule requires both email and in-platform message templates.
                </p>
              </div>

              {loading || loadingAssessments ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : rules.length === 0 ? (
                <Alert variant="info">
                  No communication rules found. Create rules first before adding templates.
                </Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Rule</th>
                      <th>Assessment</th>
                      <th>Threshold</th>
                      <th>Email Template</th>
                      <th>In-Platform Template</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(rules) && rules.map((rule) => (
                      <tr key={rule.id}>
                        <td>
                          <Badge bg={rule.active ? "success" : "secondary"}>
                            {rule.active ? "Active" : "Inactive"}
                          </Badge>
                          <div className="mt-1">{rule.eventType}</div>
                        </td>
                        <td>
                          <strong>{getAssessmentDisplay(rule.assessmentId)}</strong>
                          <div className="text-muted small">
                            <code>{rule.assessmentId}</code>
                          </div>
                        </td>
                        <td>
                          <strong>{rule.threshold}%</strong>
                        </td>
                        <td>
                          <Badge bg={rule.hasEmailTemplate ? "success" : "secondary"}>
                            <FaEnvelope style={{ marginRight: "0.3rem" }} />
                            {rule.hasEmailTemplate ? "Configured" : "Needs Setup"}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={rule.hasInPlatformTemplate ? "success" : "secondary"}>
                            <FaBell style={{ marginRight: "0.3rem" }} />
                            {rule.hasInPlatformTemplate ? "Configured" : "Needs Setup"}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => loadTemplatesForRule(rule)}
                          >
                            <FaEdit style={{ marginRight: "0.3rem" }} />
                            Edit Templates
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          ) : (
            // Editor View
            <>
              <VariableHelper />

              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                {/* Email Template Tab */}
                <Tab
                  eventKey="email"
                  title={
                    <span>
                      <FaEnvelope style={{ marginRight: "0.5rem" }} />
                      Email Template
                    </span>
                  }
                >
                  <Alert variant="info" className="mb-3">
                    <FaInfoCircle style={{ marginRight: "0.5rem" }} />
                    <strong>Note:</strong> Your email body will be automatically wrapped in ALPACA's branded email template with logo, 
                    styling, greeting, and "View Dashboard" button. Just focus on the message content.
                  </Alert>
                  
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Subject Line <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={emailFormData.subject}
                          onChange={(e) =>
                            setEmailFormData({
                              ...emailFormData,
                              subject: e.target.value,
                            })
                          }
                          placeholder="e.g., Screening Milestone Reached: {{percentage}}% Complete"
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => insertVariable("schoolName", "subject")}
                        >
                          + Var
                        </Button>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Email Body <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={12}
                        value={emailFormData.body}
                        onChange={(e) =>
                          setEmailFormData({
                            ...emailFormData,
                            body: e.target.value,
                          })
                        }
                        placeholder="Dear {{teacherName}},&#10;&#10;Great news! {{schoolName}} has reached {{percentage}}% completion...&#10;&#10;{{studentsScreened}} out of {{totalStudents}} students have completed the {{assessmentName}} screening."
                      />
                      <Form.Text className="text-muted">
                        Use variables like {`{{schoolName}}`} - they will be replaced with actual values
                      </Form.Text>
                    </Form.Group>
                  </Form>
                </Tab>

                {/* In-Platform Template Tab */}
                <Tab
                  eventKey="inPlatform"
                  title={
                    <span>
                      <FaBell style={{ marginRight: "0.5rem" }} />
                      In-Platform Template
                    </span>
                  }
                >
                  <Form>
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Title <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={inPlatformFormData.title}
                            onChange={(e) =>
                              setInPlatformFormData({
                                ...inPlatformFormData,
                                title: e.target.value,
                              })
                            }
                            placeholder="e.g., Screening Milestone: {{percentage}}% Complete"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Priority</Form.Label>
                          <Form.Select
                            value={inPlatformFormData.priority}
                            onChange={(e) =>
                              setInPlatformFormData({
                                ...inPlatformFormData,
                                priority: e.target.value,
                              })
                            }
                          >
                            {priorities.map((p) => (
                              <option key={p} value={p}>
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Message Body <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        value={inPlatformFormData.body}
                        onChange={(e) =>
                          setInPlatformFormData({
                            ...inPlatformFormData,
                            body: e.target.value,
                          })
                        }
                        placeholder="{{schoolName}} has reached {{percentage}}% completion on the {{assessmentName}} screening. {{studentsScreened}}/{{totalStudents}} students completed."
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Message Type</Form.Label>
                          <Form.Select
                            value={inPlatformFormData.messageType}
                            onChange={(e) =>
                              setInPlatformFormData({
                                ...inPlatformFormData,
                                messageType: e.target.value,
                              })
                            }
                          >
                            {messageTypes.map((t) => (
                              <option key={t} value={t}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Expires After (Days)</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            max="365"
                            value={inPlatformFormData.expiresAfterDays}
                            onChange={(e) =>
                              setInPlatformFormData({
                                ...inPlatformFormData,
                                expiresAfterDays: parseInt(e.target.value),
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Action URL (Optional)</Form.Label>
                      <Form.Control
                        type="text"
                        value={inPlatformFormData.actionUrl}
                        onChange={(e) =>
                          setInPlatformFormData({
                            ...inPlatformFormData,
                            actionUrl: e.target.value,
                          })
                        }
                        placeholder="/dashboard or * for all pages"
                      />
                      <Form.Text className="text-muted">
                        Page where this message should appear. Use '*' for all pages.
                      </Form.Text>
                    </Form.Group>
                  </Form>
                </Tab>
              </Tabs>

              <div className="d-flex justify-content-between mt-4">
                <Button variant="secondary" onClick={handleBack}>
                  <FaTimes style={{ marginRight: "0.5rem" }} />
                  Back to List
                </Button>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={handlePreview}
                    disabled={previewLoading}
                  >
                    {previewLoading ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        style={{ marginRight: "0.5rem" }}
                      />
                    ) : (
                      <FaEye style={{ marginRight: "0.5rem" }} />
                    )}
                    Preview
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveTemplates}
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        style={{ marginRight: "0.5rem" }}
                      />
                    ) : (
                      <FaCheck style={{ marginRight: "0.5rem" }} />
                    )}
                    Save Templates
                  </Button>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {!showEditor && (
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Template Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preview && preview.email && preview.inPlatform ? (
            <>
              <h5>
                <FaEnvelope style={{ marginRight: "0.5rem" }} />
                Email Preview
              </h5>
              <Card className="mb-3">
                <Card.Body style={{ backgroundColor: "#fcf7e4" }}>
                  <div style={{ 
                    maxWidth: "600px", 
                    margin: "0 auto", 
                    padding: "20px", 
                    backgroundColor: "#ffffff",
                    borderRadius: "10px",
                    border: "1px solid #ddd"
                  }}>
                    {/* Logo placeholder */}
                    <div style={{ 
                      backgroundColor: "#e8e8e8", 
                      padding: "10px", 
                      textAlign: "center",
                      marginBottom: "20px",
                      borderRadius: "5px"
                    }}>
                      <small className="text-muted">🦙 ALPACA Logo</small>
                    </div>
                    
                    <h3 style={{ textAlign: "center", marginBottom: "20px", color: "#333" }}>
                      Milestone Achievement!
                    </h3>
                    
                    <div className="mb-3">
                      <strong>Subject:</strong> {preview.email.subject}
                    </div>
                    
                    <p style={{ fontSize: "18px", marginBottom: "15px" }}>
                      Hello Ms. Smith,
                    </p>
                    
                    <div style={{ 
                      fontSize: "18px", 
                      whiteSpace: "pre-wrap",
                      marginBottom: "20px"
                    }}>
                      {preview.email.body}
                    </div>
                    
                    {/* Dashboard button */}
                    <div style={{ textAlign: "center", margin: "30px 0" }}>
                      <Button variant="success" disabled>
                        View Dashboard
                      </Button>
                    </div>
                    
                    <p style={{ fontSize: "18px", marginTop: "30px" }}>
                      We're always here to support. You can email{" "}
                      <a href="mailto:info@alpaca-assessment.com">info@alpaca-assessment.com</a>
                    </p>
                    
                    <p className="text-muted" style={{ marginTop: "20px" }}>
                      Example Elementary School
                    </p>
                    
                    <div style={{ marginTop: "30px" }}>
                      <p style={{ marginBottom: "5px" }}>Thanks a bunch!</p>
                      <p style={{ marginBottom: "5px" }}>Warm regards,</p>
                      <p style={{ fontWeight: "bold" }}>Alfie the ALPACA</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <h5>
                <FaBell style={{ marginRight: "0.5rem" }} />
                In-Platform Message Preview
              </h5>
              <Card>
                <Card.Body>
                  <div className="mb-2">
                    <strong>Title:</strong> {preview.inPlatform.title}
                    <Badge
                      bg={
                        preview.inPlatform.priority === "urgent"
                          ? "danger"
                          : preview.inPlatform.priority === "high"
                          ? "warning"
                          : "info"
                      }
                      className="ms-2"
                    >
                      {preview.inPlatform.priority}
                    </Badge>
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {preview.inPlatform.body}
                  </div>
                  <div className="mt-2 text-muted">
                    <small>
                      Type: {preview.inPlatform.messageType} | Expires:{" "}
                      {preview.inPlatform.expiresAt
                        ? new Date(preview.inPlatform.expiresAt).toLocaleDateString()
                        : "Never"}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div className="text-center py-3">
              <Spinner animation="border" />
              <p className="text-muted mt-2">Loading preview...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ManageTemplatesModal;